import { useCallback, useState } from "react";
import { FmSelectionActionProps } from "domains/file-manager/components/FileManagerV2/FmSelectionActions";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import Button from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  useCopyAssetByAssetIdMutation,
  usePutAssetByAssetIdMutation,
} from "infra/api/generated/api";

export type SelectionActionDuplicateProps = FmSelectionActionProps<"canvas"> & {
  isDisplayed?: boolean;
  isDisabled?: boolean;
};

export default function SelectionActionDuplicate({
  selectedFiles,
  isDisplayed,
  isDisabled,
}: SelectionActionDuplicateProps) {
  const { selectedProject } = useTeamContext();
  const { showLimitModal } = usePlanContext();
  const handleApiError = useHandleApiError();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [putAssetTrigger] = usePutAssetByAssetIdMutation();
  const [copyAssetTrigger] = useCopyAssetByAssetIdMutation();

  const handleDuplicateClick = useCallback(async () => {
    setIsDuplicating(true);
    for (const file of selectedFiles) {
      try {
        const newAsset = await copyAssetTrigger({
          projectId: selectedProject.id,
          assetId: file.meta.id,
        }).unwrap();

        await putAssetTrigger({
          projectId: selectedProject.id,
          assetId: newAsset.asset.id,
          body: {
            name: `Copy of ${file.meta.metadata.name}`,
          },
        });

        Track(AnalyticsEvents.Canvas.Duplicate, {
          assetId: file.meta.id,
        });
      } catch (error) {
        handleApiError(error, "There was an error duplicating the canvas", {
          quota: async () => {
            showLimitModal("planCanvasProjects");
          },
        });
      }
    }
    setIsDuplicating(false);
  }, [
    selectedFiles,
    copyAssetTrigger,
    selectedProject.id,
    putAssetTrigger,
    handleApiError,
    showLimitModal,
  ]);

  return (
    <Button
      isLoading={isDuplicating}
      leftIcon={<Icon id="Ui/Copy" h="16px" />}
      onClick={handleDuplicateClick}
      isDisabled={isDisabled || !isDisplayed}
      variant="secondary"
    >
      Duplicate
    </Button>
  );
}
